/**
 * Types for the licence API (#635): `/api/licensing/` — the state list upload (preview, column
 * choice, load), the People → Licences counts and the load history. Wire shapes are the backend's
 * (`apps.licensing.views`); every response is counts and sentences, never a member's name.
 */

/** The four counts of People → Licences, each the `licenseState` filter that lists them. */
export type LicenseState = "expiring90" | "expired" | "late_renewal" | "suspended_limited";

export const LICENSE_STATES: readonly LicenseState[] = ["expiring90", "expired", "late_renewal", "suspended_limited"];

/** One load, as the history and the overview show it. */
export interface LicenseImport {
  id: number;
  importedAt: string;
  importedBy: string;
  filename: string;
  sha256: string;
  rows: number;
  matchedLicense: number;
  matchedNameCity: number;
  ambiguous: number;
  /** Held back: a licence number on a CE21 person of another name (absent on loads before 28 Oct 2026). */
  nameMismatch?: number;
  unmatched: number;
  unknownStatuses: number;
  statuses: Record<string, number>;
  effectiveChanges: Partial<EffectiveChanges>;
  overriddenChecks: string[];
  /** The checks that warned on this load (absent on loads from before 28 Oct 2026). */
  warnedChecks?: string[];
  /** "What will change" lines the loader unticked: left as they were. */
  excludedLines?: number;
  excludedBy?: string;
  /** "What will change" lines the review listed (absent before 29 Oct 2026). */
  changeLines?: number;
  /** Rows loaded onto a person by Tim's Match or Choose. */
  decidedMatches?: number;
}

export interface LicenseOverview {
  asOf: string;
  denominator: number;
  counts: Array<{ key: LicenseState; count: number; filter: { licenseState: LicenseState } }>;
  lastImport: LicenseImport | null;
  /** People → State emails: members with a made-up or empty CE21 email whom the latest list has an address for. */
  stateEmails: number;
  /** People → Fix in CE21: the people Tim flagged for CE21 (#635, 29 Oct 2026). */
  ce21Fixes?: number;
  /** The latest load's rows still to decide. */
  toDecide?: number;
}

/** One person on People → Fix in CE21: flagged by Tim, with why and when. PII. */
export interface Ce21FixPerson {
  memberId: number;
  name: string;
  email: string;
  status: string;
  licenseNo: string;
  why: string;
  flaggedAt: string;
  flaggedBy: string;
}

export interface Ce21Fixes {
  count: number;
  people: Ce21FixPerson[];
}

/** One person on People → State emails: CE21's made-up (or empty) address beside the state list's. PII. */
export interface StateEmailPerson {
  memberId: number;
  name: string;
  status: string;
  /** "" when CE21 has none. */
  ce21Email: string;
  stateEmail: string;
  reason: "placeholder" | "empty";
}

export interface StateEmails {
  count: number;
  people: StateEmailPerson[];
}

export interface LicenseImportHistory {
  results: LicenseImport[];
}

export type CheckLevel = "pass" | "warn" | "block";

export interface ColumnOption {
  column: number;
  header: string;
}

export interface LicenseCheck {
  key: string;
  level: CheckLevel;
  /** One plain sentence. */
  message: string;
  count: number | null;
  /** A block the loader may accept by ticking it. */
  confirmable: boolean;
  /** A block settled by choosing a column for `field` from `options`. */
  choosable: boolean;
  field: string | null;
  options: ColumnOption[];
  /** What it compares: the file itself, the file against CE21 and Keystone, or against the last list loaded. */
  group: CheckGroup;
  /** Whether its count opens its rows in the review (`GET uploads/<token>/review`). */
  opens: boolean;
}

export type CheckGroup = "file" | "ce21" | "since";

/** One row a check counted. PII: the review is a People view's rows. */
export interface ReviewItem {
  name: string;
  licenseNo: string;
  memberId: number | null;
  /** The value on each side, as the check's `from` / `to` labels say ("CE21 expiry" → "State expiry"); ISO for a date. */
  from: string;
  to: string;
  /** A direction tag ("later", "earlier", "ce21_empty", "later jump", "gone"…) or "". */
  tag: string;
  /** A row Tim decides (`DECIDES`): its handle, and his decision (null: still to decide). */
  ref?: string;
  key?: string;
  subject?: number;
  decision?: Decision | null;
  /** Not in CE21: the people Keystone has who may be this licensee (Match), and the person already added from the list. */
  candidates?: PersonRef[];
  addedId?: number | null;
  /** Licence on two people: who carries the number (Choose the right person). */
  sharers?: PersonRef[];
}

export interface PersonRef {
  memberId: number;
  name: string;
  licenseNo: string;
}

/** What Tim decides on a row. */
export type DecisionAction = "added" | "matched" | "checked" | "flagged" | "chosen";
export type CheckedReason = "retired" | "moved" | "other";

export interface Decision {
  action: DecisionAction;
  reason: string;
  memberId: number | null;
  memberName: string;
  by: string;
  at: string;
}

/** `POST /api/licensing/decisions`: on an upload (`token`) or the latest load (`importId`). */
export interface DecisionRequest {
  token?: string;
  importId?: number;
  check: string;
  action: DecisionAction | "undo";
  refs: string[];
  memberId?: number;
  reason?: string;
}

export interface DecisionAnswer {
  result: { decided: number; added: number; present: number; refused: Array<{ ref: string; why: "email_taken"; memberId: number | null; name: string }> };
  /** The decided checks as they are now; the whole review after a Match or a Choice on an upload. */
  review: Pick<UploadReview, "checks"> & Partial<UploadReview>;
  /** After a Match or a Choice on an upload: the upload read again (same token). */
  report?: UploadPreview;
}

/** `GET /api/licensing/imports/latest/review`: the latest load's checks Tim decides, to decide after the load. */
export interface LatestReview {
  importId: number;
  importedAt: string;
  checks: LicenseCheck[];
  review: Pick<UploadReview, "checks">;
}

/** A number far outside a normal month, said in one line. */
export interface Unusual {
  key: "gone" | "changes";
  sentence: string;
}

export interface ReviewRows {
  from: string;
  to: string;
  items: ReviewItem[];
  /** More rows than the review lists (the count is still exact). */
  truncated: boolean;
}

export type ChangeField = "expiry" | "status";
/** `held`: the member's licence number now names a person of another name, so last load's state values are cleared. */
export type ChangeDirection = "later" | "ce21_empty" | "earlier" | "same" | "gone" | "no_date" | "status" | "held";

/** Why a line changes what Keystone shows: the shown expiry moves later, is newly known, moves earlier (worth a check), the status changes, or the person is gone from the list or held back. */
export type ChangeWhy = "renewed" | "new" | "earlier" | "status" | "gone" | "held" | "no_date";

/** One person and field whose shown value (the displayed expiry, or the state status) the load would change in Keystone. Unticked, it is left as it was. PII. */
export interface ChangeLine {
  /** `"<member id>:<field>"`: what Load's `exclude` names. */
  id: string;
  memberId: number;
  name: string;
  licenseNo: string;
  field: ChangeField;
  /** CE21's expiry (ISO; "" when CE21 has none, and for a status). */
  ce21: string;
  /** The state's value Keystone holds now, and after the load. */
  before: string;
  after: string;
  /** Keystone's displayed expiry (the later of the state's and CE21's) before and after; the status for a status line. */
  keystoneBefore: string;
  keystoneAfter: string;
  direction: ChangeDirection;
  /** Absent on a review stored before 29 Oct 2026: `whyOf` reads it from `direction`. */
  why?: ChangeWhy;
  /** A move of more than 2.5 years. */
  jump: boolean;
  /** The checks that list this person. */
  checks: string[];
}

/** `GET /api/licensing/uploads/<token>/review`: the rows each check opens, and what will change. */
export interface UploadReview {
  checks: Record<string, ReviewRows>;
  /** Only the lines that change what Keystone shows. */
  lines: ChangeLine[];
  /** State values the load moves while what Keystone shows stays as it is ("332 already match"): loaded, never listed. */
  unchanged: number;
  /** Numbers far outside a normal month (absent on a review stored before 29 Oct 2026). */
  unusual?: Unusual[];
}

export interface FieldMapping {
  field: string;
  label: string;
  required: boolean;
  column: number | null;
  header: string;
  confidence: number;
  method: "header" | "header_loose" | "content" | "chosen" | "";
  note: string;
  alternatives: ColumnOption[];
}

export interface EffectiveChanges {
  later: number;
  earlier: number;
  added: number;
  cleared: number;
  total: number;
  newlyExpired: number;
  matched: number;
}

/** The column that said which rows are chiropractors (the state's whole report), and what it left out. Counts only. */
export interface ProfessionFilter {
  column: number;
  header: string;
  method: "header" | "content";
  /** Chiropractor rows kept; 0 means the column names no chiropractor and nothing loads. */
  kept: number;
  leftOutTotal: number;
  /** Profession → rows left out, the largest first ("(other professions)" sums the rest). */
  leftOut: Record<string, number>;
  professions: number;
  /** The broader profession column beside the chosen licence-type one (the state's Profession beside its Type), and the rows left out by its values; null when there is none. */
  groups?: { header: string; leftOut: Record<string, number> } | null;
}

/** What a preview (and a load) says about the file. */
export type UploadStatus = "ready" | "needs_confirmation" | "needs_choice" | "blocked" | "duplicate" | "loaded";

export interface UploadReport {
  status: UploadStatus;
  file: { filename: string; sha256: string };
  duplicateOfImport: number | null;
  mapping: {
    format: "xlsx" | "csv";
    sheet: string;
    headerRow: number;
    encoding: string;
    fields: FieldMapping[];
    ignoredColumns: string[];
    otherSheets: string[];
    columnsTruncated: boolean;
    /** Every data row in the file, before the profession filter. */
    fileRows: number;
    profession: ProfessionFilter | null;
  };
  checks: LicenseCheck[];
  summary: {
    rows: number;
    fileRows: number;
    profession: ProfessionFilter | null;
    matchedLicense: number;
    matchedNameCity: number;
    ambiguous: number;
    nameMismatch?: number;
    unmatched: number;
    statuses: Record<string, number>;
    unknownStatuses: number;
    effectiveChanges: Partial<EffectiveChanges>;
    overriddenChecks: string[];
    chosenColumns: Record<string, number>;
    warnedChecks?: string[];
    excludedLines?: number;
  };
}

/** A preview: the report, and the token that loads it (null when the file can never load or is loaded already). */
export interface UploadPreview extends UploadReport {
  token: string | null;
  expiresAt: string | null;
  expiresInMinutes: number;
}

/** The upload's answer while the server still reads the file (`202`): poll `GET /api/licensing/uploads/<token>`. */
export interface UploadChecking {
  status: "checking";
  token: string;
  file: { filename: string; size: number };
  /** false while the file waits for the server's background reader. */
  reading: boolean;
  startedAt: string;
}

/** What one Load click came to. */
export type LoadOutcome =
  /** Loaded (or found already loaded): the report, and the import when one was written. */
  | { kind: "loaded"; report: UploadReport & { import?: LicenseImport } }
  /** Something changed since the preview: a new report and a new token to review. */
  | { kind: "review"; preview: UploadPreview; detail: string }
  /** A block that was shown is not ticked (or needs a column): the same token, the current checks. */
  | { kind: "blocked"; report: UploadReport; detail: string }
  /** Another list was loaded since, or the upload expired: start again. */
  | { kind: "again"; detail: string }
  /** A request the server could not take: its one sentence. */
  | { kind: "error"; detail: string };
